import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "commons/store/store";
import {
    EcrfEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead,
    ElementDataEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead,
    ElementDataElementDataElementDataReadIdReadStatusReadCommentRead,
    ElementDependency,
    SectionSectionSectionReadSectionElementsIdReadNameReadPositionReadStatusReadElement,
    VisitVisitVisitReadIdReadNameReadPositionRead,
} from "types/api.types";
import axiosSecureInstance from "commons/axios/axiosSecureInstance";
import { HalResource } from "../../../types/halResource.types";

export interface SurveyState {
    // TODO swagger model for survey
    survey: any;
    segments: VisitVisitVisitReadIdReadNameReadPositionRead[];
    elements: ElementDataEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead[];
    currentSection: any | null;
    queries: any;
    selectedSection: any;
    dependencies: any;
    collectionDependencies: any;
    adverseEvents: any;
    selectedCollection: fetchCollectionParams | null;
    selectedElementDatas: any;
    addedElementData: any;
    editedElementData: any;
    elementDataCollection: any;
    elementDataCollections: any;
    collectionElementDatas: any;
    selectedElement: any;
    adverseEventsFields: any;
    randomize: any;
}

const initialState: SurveyState = {
    survey: null,
    segments: [],
    elements: [],
    currentSection: null,
    queries: {},
    selectedSection: {},
    dependencies: [],
    collectionDependencies: [],
    adverseEvents: {},
    selectedCollection: null,
    selectedElementDatas: {},
    addedElementData: {},
    editedElementData: {},
    elementDataCollection: {},
    elementDataCollections: {},
    collectionElementDatas: {},
    selectedElement: {},
    adverseEventsFields: [],
    randomize: null,
};

export type SurveyFormParams = {
    id: string;
    section: string;
};

export const fetchSurvey = createAsyncThunk('survey/fetchSurvey', async (id: string) => {
    const response =
        await axiosSecureInstance.get<EcrfEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead>(
            `/api/surveys/${id}`
        );

    return response.data;
});

export const fetchSegments = createAsyncThunk(
    "survey/fetchSegments",
    async (researchId?: string | null | undefined) => {
        const response = await axiosSecureInstance.get<VisitVisitVisitReadIdReadNameReadPositionRead[]>(
            `/api/segments`,
            {
                params: {
                    research: researchId,
                },
            }
        );
        return response.data;
    }
);

/* Endpoint for survey elements */
export const fetchSurveyElements = createAsyncThunk(
    "survey/fetchSurveyElements",
    async ({ id, sectionId }: any, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get<ElementDataEcrfEcrfReadIdReadSectionReadCommentReadContentReadStatusReadOwnerRead[]>(
                `/api/surveys/${id}/elements`,
                {
                    params: {
                        'element.section': sectionId,
                    },
                }
            );
            return response.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
)

export const fetchSegmentsWithCounters = createAsyncThunk(
    "survey/fetchSegmentsWithCounters",
    async (id: string | undefined, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get<any>(`/api/segments/${id}/survey`)
            return response.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
)

export const fetchSection = createAsyncThunk(
    "survey/fetchSection",
    async (id: string, thunkAPI) => {
    try {
        const response =
            await axiosSecureInstance.get<SectionSectionSectionReadSectionElementsIdReadNameReadPositionReadStatusReadElement>(
                `/api/sections/${id}`
            );
        return response.data;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const fetchSectionBySurvey = createAsyncThunk(
    'survey/fetchSection',
    async ({ id, surveyId }: { id: string, surveyId: string | undefined }, thunkAPI) => {
    try {
        const response =
            await axiosSecureInstance.get<SectionSectionSectionReadSectionElementsIdReadNameReadPositionReadStatusReadElement>(
                `/api/sections/${id}`,
                { params: { survey: surveyId  } }
            );
        return response.data;
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

type PostElementDataParams = {
    element: string;
    survey: string;
    data?: Array<any>;
    children?: Array<any>;
    comment?: any;
    collection?: Array<any>;
    entries?: Array<any>;
    status?: number;
};

/**
 * postElementData()
 * @deprecated Use postElementData (elementDataSlice.ts)
 */
export const postElementData = createAsyncThunk(
    'survey/postElementData',
    async (params: PostElementDataParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.post(`/api/element_datas`, params);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

// TODO swagger model for element values
type PutElementDataParams = {
    elementId: string;
    surveyId: string;
    data: Array<any>;
    status?: number;
    comment?: any;
};

/**
 * putElementData()
 * @deprecated Use putElementData (elementDataSlice.ts)
 */
export const putElementData = createAsyncThunk(
    'survey/putElementData',
    async ({ elementId, surveyId, data, comment }: PutElementDataParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.put(`/api/element_datas/element/${elementId}`,
            // const response = await axiosSecureInstance.patch(`/api/element_datas/element/${elementId}`,
              {
                data,
                survey: `/api/surveys/${surveyId}`,
                comment: comment
              },
              // {
              //   headers: {
              //     'Content-Type': 'application/merge-patch+json'
              //   }
              // }
            );
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

export const putDataToElement = createAsyncThunk(
    "survey/putDataToElement",
    async ({elementDataId, data}: any, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.put(`/api/element_datas/${elementDataId}`, {
                data,
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type UpdateElementDataParams = {
    id?: string;
    element?: string;
    survey?: string;
    data?: Array<any>;
    comment?: any;
    collection?: Array<any>;
    entries?: Array<any>;
    status?: number;
};

export const updateElementData = createAsyncThunk(
    'survey/updateElementData',
    async (params: UpdateElementDataParams, thunkAPI) => {
        try {
            const id = params?.id
            delete params?.id

            const response = await axiosSecureInstance.patch(`/api/element_datas/${id}`, params);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type ProvideDrugParams = {
    id: string;
    drugs: Array<any>;
};

export const provideDrug = createAsyncThunk(
    'survey/provideDrug',
    async ({id, drugs}: ProvideDrugParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.put(`/api/surveys/${id}/drug/provide`, {
                drugs,
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type updateElementStatusParams = {
    elementId: string;
    status: number;
    surveyId: string;
};

export const updateElementStatus = createAsyncThunk(
    "survey/putElementData",
    async ({ elementId, surveyId, status }: updateElementStatusParams, thunkAPI) => {
      try {
        const response = await axiosSecureInstance.put(`/api/element_datas/element/${elementId}`, {
            status,
            survey: `/api/surveys/${surveyId}`,
        });
        return response.data;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(error.response.data);
      }
    }
);

export const fetchQueries = createAsyncThunk(
    'survey/fetchQueries',
    async (id: string | null, thunkAPI) => {
      try {
        const response = await axiosSecureInstance.get<any[]>(`/api/comments/${id}`);
        return response.data;
      } catch (error: any) {
        return thunkAPI.rejectWithValue(error.response.data);
      }
    }
);

export const fetchDependencies = createAsyncThunk(
    'survey/fetchDependencies',
    async (sectionId: string | undefined, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get<ElementDependency[]>(
                `/api/element_dependencies`,
                {
                    params: {
                        'parents.section': sectionId,
                    },
                }
            );
            return response.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
)

export const fetchCollectionDependencies = createAsyncThunk(
    'survey/fetchCollectionDependencies',
    async (elementId: string | undefined, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get<ElementDependency[]>(
                `/api/element_dependencies`,
                {
                    params: {
                        'parents.childWithElement': elementId,
                    },
                }
            );
            return response.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
)

export const fetchDependency = createAsyncThunk(
  "survey/fetchDependency",
  async (id: string) => {
    const response = await axiosSecureInstance.get<any>(`/api/element_dependencies/${id}`);
    return response.data;
});

type addQueryParams = {
    element?: string;
    parent?: string;
    content: string;
};

export const addQuery = createAsyncThunk(
    "survey/addQuery",
    async ({content, parent, element}: addQueryParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.post(`/api/comments`, {
                element,
                parent,
                content,
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type AdverseEventsParams = {
    // page: number;
    // randomId?: string | null | undefined;
};

export type ButtonOptions = {
    buttonText: string;
    confirmText?: string;
    confirmDisabledText?: string;
    alertText?: string;
    elementData: any;
    buttonDisabledText?: string;
    description?: string;
    type?: string | undefined;
    disabledCondition?: string;
    modal?: boolean;
    autosave?: boolean;
};

export type fetchCollectionParams = {
    id?: string | null
    children?: any[]
    collectionId?: string
    elements?: any[]
    dataId?: string
    surveyId: string
    elementId?: string
    elementName: string
    elementType?: string
    sectionId?: string
    status: any
    options?: ButtonOptions;
    type: 'add' | 'clone' | 'create' | 'edit' | 'duplicate' | 'save'
};

export const fetchCollection = createAsyncThunk('survey/fetchCollection', (row: fetchCollectionParams | null) => {
    return Object.assign({}, row);
});

export const fetchElementDatas = createAsyncThunk(
    'survey/fetchElementDatas',
    async (id: string, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get(`/api/element_datas/${id}`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type CollectionElementDatasParams = {
    elementId: string | null | undefined;
    surveyId?: string | null | undefined;
};

export const fetchCollectionElementDatas = createAsyncThunk(
    'survey/fetchCollectionElementDatas',
    async ({ elementId, surveyId }: CollectionElementDatasParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get<any[]>(
                `/api/element_datas`,
                {
                    params: {
                        'element.childWithElement': elementId,
                        'survey': surveyId,
                    },
                }
            );
            return response.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
)

export const fetchElementDatasChildren = createAsyncThunk(
    'survey/fetchElementDatasChildren',
    async (id: string, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get<any[]>(
                `/api/element_datas/${id}/children`
            );
            return response.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
)

export const fetchElement = createAsyncThunk("survey/fetchElement", async (elementId: string) => {
    const response = await axiosSecureInstance.get(`/api/elements/${elementId}`);
    return response.data;
});

type editCollectElementDataParams = {
    dataId: string;
    collections: Array<any>;
};

export const editCollectElementData = createAsyncThunk(
    'survey/editCollectElementData',
    async ({dataId, collections}: editCollectElementDataParams) => {
        const response = await axiosSecureInstance.put(`/api/element_datas/${dataId}`, {
            data: [0],
            collection: [...collections],
        });
        return response.data;
    }
);

export const deleteElementDataCollection = createAsyncThunk(
    'survey/deleteElementDataCollection',
    async (id: string, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.delete(`/api/element_data_collections/${id}`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type FetchElementDataCollectionsParams = {
    id: string;
    params?: any;
};

export const fetchElementDataCollections = createAsyncThunk(
    'survey/fetchElementDataCollections',
    async ({id, params = []}: FetchElementDataCollectionsParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get(
                `/api/element_datas/${id}/collections`,
                {
                    params: params,
                }
            );
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

export const fetchElementDataCollection = createAsyncThunk(
    'survey/fetchElementDataCollection',
    async (id: string, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.get(`/api/element_data_collections/${id}`);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type PostElementDataCollectionsParams = {
  datas: Array<any>;
  elements?: Array<any>;
};

export const postElementDataCollections = createAsyncThunk(
    'survey/postElementDataCollections',
    async ({datas, elements = []}: PostElementDataCollectionsParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.post(`/api/element_data_collections`, {
                datas: [...datas],
                elements: [...elements],
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type PutCollectElementDataParams = {
    id: string | number;
    elements: Array<any>;
};

export const putCollectElementData = createAsyncThunk(
    'survey/putCollectElementData',
    async ({id, elements}: PutCollectElementDataParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.put(`/api/element_data_collections/${id}`, {
                elements: [...elements],
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type PostElementDataCollectionParams = {
    elementId: string;
    surveyId: string;
    elements?: Array<any>;
};

export const postElementDataCollection = createAsyncThunk(
    'survey/postElementDataCollection',
    async ({elementId, surveyId, elements}: PostElementDataCollectionParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.post(`/api/element_datas`, {
                data: [],
                survey: `/api/surveys/${surveyId}`,
                element: `/api/elements/${elementId}`,
                collection: [
                    {
                        elements,
                    },
                ],
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type putElementDataCollectionParams = {
    dataId: string;
    collections: Array<any>;
};

export const putElementDataCollection = createAsyncThunk(
    'survey/putElementDataCollection',
    async ({dataId, collections}: putElementDataCollectionParams, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.put(`/api/element_datas/${dataId}`, {
                collection: [...collections],
            });
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

type ElementDataManagementParams = {
    // page: number;
    // randomId?: string | null | undefined;
    // segmentId?: string | null | undefined;
};

export const fetchElementDataManagement = createAsyncThunk(
    'survey/fetchElementDataManagement',
    async (config: ElementDataManagementParams, thunkAPI) => {
        try {
            // const response = await axiosSecureInstance.get<HalResource<ElementDataElementDataElementDataReadIdReadStatusReadCommentRead[]>>
            // (`/api/element_datas/data_management`, {
            //     headers: {
            //         accept: 'application/hal+json',
            //     },
            //     params: {
            //         pagination: true,
            //         page,
            //         itemsPerPage: 100,
            //         'survey.randomId': randomId,
            //         'element.section.segment': segmentId,
            //         // 'order[updatedAt]': 'desc',
            //         // 'order[survey.participantNumber]': 'asc',
            //         // 'order[element.position]': 'asc',
            //     },
            // });
            const response = await axiosSecureInstance.get<HalResource<ElementDataElementDataElementDataReadIdReadStatusReadCommentRead[]>>
            (`/api/element_datas/data_management`, config);
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.response.data);
        }
    }
);

export const randomizePatient = createAsyncThunk(
    'survey/randomize',
    async (surveyId: string, thunkAPI) => {
        try {
            const response = await axiosSecureInstance.put<any>(`/api/surveys/${surveyId}/randomize`, {});
            return response.data;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data);
        }
    }
);

export const surveySlice = createSlice({
    name: 'survey',
    initialState,
    reducers: {
        getSectionId(state, action: PayloadAction<any>) {
            state.selectedSection = action.payload;
        },
        resetSurvey(state) {
            state.survey = initialState.survey;
        },
        resetAdverseEvents(state) {
            state.adverseEvents = initialState.adverseEvents;
        },
        resetCurrentSection(state) {
            state.currentSection = initialState.currentSection;
        },
        resetElementDatas(state) {
            state.selectedElementDatas = initialState.selectedElementDatas;
        },
        resetEditElementData(state) {
            state.editedElementData = initialState.editedElementData;
        },
        resetElementDataCollection(state) {
            state.elementDataCollection = initialState.elementDataCollection;
        },
        resetElementDataCollections(state) {
            state.elementDataCollections = initialState.elementDataCollections;
        },
        resetCollection(state) {
            state.selectedCollection = initialState.selectedCollection;
        },
        resetDependencies(state) {
            state.dependencies = initialState.dependencies;
        },
        resetQueries(state) {
            state.queries = initialState.queries
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchSurvey.fulfilled, (state: SurveyState, action) => {
            state.survey = action.payload;
        });
        builder.addCase(fetchSegmentsWithCounters.fulfilled, (state, action) => {
            state.segments = action.payload;
        });
        builder.addCase(fetchSurveyElements.fulfilled, (state, action) => {
            state.elements = action.payload;
        });
        builder.addCase(fetchSection.fulfilled, (state, action) => {
            state.currentSection = action.payload;
        });
        builder.addCase(fetchQueries.fulfilled, (state, action) => {
            state.queries = action.payload;
        });
        builder.addCase(fetchDependencies.fulfilled, (state, action) => {
            state.dependencies = action.payload;
        });
        builder.addCase(fetchCollectionDependencies.fulfilled, (state, action) => {
            state.collectionDependencies = action.payload;
        });
        builder.addCase(fetchCollection.fulfilled, (state, action) => {
            state.selectedCollection = action.payload;
        });
        builder.addCase(fetchElementDatas.fulfilled, (state, action) => {
            state.selectedElementDatas = action.payload;
        });
        builder.addCase(postElementData.fulfilled, (state, action) => {
            state.editedElementData = action.payload;
        });
        builder.addCase(putElementData.fulfilled, (state, action) => {
            state.editedElementData = action.payload;
        });
        builder.addCase(fetchElementDataCollection.fulfilled, (state, action) => {
            state.elementDataCollection = action.payload;
        });
        builder.addCase(fetchElementDataCollections.fulfilled, (state, action) => {
            state.elementDataCollections = action.payload;
        });
        builder.addCase(fetchCollectionElementDatas.fulfilled, (state, action) => {
            state.collectionElementDatas = action.payload;
        });
        builder.addCase(fetchElement.fulfilled, (state, action) => {
            state.selectedElement = action.payload;
        });
    },
});

export const selectSurvey = (state: RootState) => state.survey.survey.survey;

export const selectSurveyElements = (state: RootState) => state.survey.survey.elements;

export const selectSegments = (state: RootState) => state.survey.survey.segments;

export const selectDependencies = (state: RootState) => state.survey.survey.dependencies;

export const selectCurrentSection = (state: RootState) => state.survey.survey.currentSection;

export const selectQueries = (state: RootState) => state.survey.survey.queries;

export const selectSectionId = (state: RootState) => state.survey.survey.selectedSection;

export const selectCollection = (state: RootState) => state.survey.survey.selectedCollection;

export const selectElementDatas = (state: RootState) => state.survey.survey.selectedElementDatas;

export const editedElementData = (state: RootState) => state.survey.survey.editedElementData;

export const selectElementDataCollection = (state: RootState) => state.survey.survey.elementDataCollection;

export const {
    getSectionId,
    resetSurvey,
    resetCurrentSection,
    resetElementDatas,
    resetElementDataCollection,
    resetElementDataCollections,
    resetCollection,
    resetDependencies,
    resetQueries
} = surveySlice.actions;

export default surveySlice.reducer;
